import gql from 'graphql-tag'

export const SUBMIT_PROVIDER_CORRECTION = gql`
  mutation SubmitProviderCorrection(
    $providerId: ID!
    $partnerId: ID
    $name: String!
    $email: String!
    $comment: String!
  ) {
    submitProviderCorrection(
      providerId: $providerId
      partnerId: $partnerId
      name: $name
      email: $email
      comment: $comment
    )
  }
`

const locationFragment = gql`
  fragment locationFields on Location {
    __typename
    id
    isClaimed
    name
    street
    city
    state
    zip
    timezone
    phoneNumber
    lat
    lng
    dolphinBookingUrl
    languagesSpoken
    displayName
    bookingExperience
    lastUpdatedAt
    lastVerifiedAt
    wheelchairAccessibleParking
    wheelchairAccessibleEntrance
    wheelchairAccessibleRestroom
    wheelchairAccessibleSeating
    officeHours {
      day
      closed
      open
      close
    }
  }
`

// Search only needs fields required to render the map and result cards. Detail
// fields stay in locationFragment and are fetched by LOCATION/PRACTICE queries.
const searchLocationFragment = gql`
  fragment searchLocationFields on Location {
    __typename
    id
    isClaimed
    name
    street
    city
    state
    zip
    timezone
    phoneNumber
    lat
    lng
    languagesSpoken
    displayName
    bookingExperience
    officeHours {
      day
      closed
      open
      close
    }
  }
`
const practiceFragment = gql`
  fragment practiceFields on Practice {
    __typename
    id
    name
    isClaimed
  }
`
const providerFragment = gql`
  fragment providerFields on Provider {
    __typename
    bio
    id
    name
    newCustomerFormLink
    photoS3Key
    websiteLink
    languagesSpoken
    credentials
    npiNumber
    providerLocations {
      id
      acceptingNewPatients
      telehealthAvailable
      location {
        ...locationFields
        practice {
          ...practiceFields
        }
      }
      partnerTags {
        __typename
        id
        name
        description
        hexColor
      }
    }
  }
`
const providerSearchFragment = gql`
  fragment providerSearchFields on Provider {
    __typename
    bio
    id
    name
    photoS3Key
    languagesSpoken
    providerLocations {
      __typename
      id
      acceptingNewPatients
      telehealthAvailable
      location {
        __typename
        id
      }
      partnerTags {
        __typename
        id
        name
        description
        hexColor
      }
    }
  }
`
const partnerTierFragment = gql`
  fragment partnerTierFields on Provider {
    partnerTier(partnerId: $partnerId) {
      __typename
      name
      description
      hexColor
      rank
    }
  }
`
const partnerTagFragment = gql`
  fragment partnerTagFields on PartnerTag {
    __typename
    id
    name
    description
    hexColor
  }
`
export const LOCATION = gql`
  query LocationQuery($id: ID!) {
    location(id: $id) {
      ...locationFields
      practice {
        ...practiceFields
      }
      providers {
        ...providerFields
        specialties {
          __typename
          name
        }
      }
      partnerTags {
        ...partnerTagFields
      }
    }
  }
  ${locationFragment}
  ${practiceFragment}
  ${providerFragment}
  ${partnerTagFragment}
`
export const PRACTICE = gql`
  query PracticeQuery($id: ID!) {
    practice(id: $id) {
      ...practiceFields
      locations {
        ...locationFields
        practice {
          ...practiceFields
        }
        providers {
          ...providerFields
          specialties {
            __typename
            name
          }
        }
      }
    }
  }
  ${practiceFragment}
  ${locationFragment}
  ${providerFragment}
`
export const PARTNER = gql`
  query PartnerQuery($id: ID!) {
    partner(id: $id) {
      __typename
      id
      name
      findAProviderConfig {
        __typename
        isAcceptsOnlineBookingFilterEnabled
        isAppointmentDateFilterEnabled
        isAppointmentTimeFilterEnabled
        isDirectionsButtonEnabled
        isProviderNameFilterEnabled
        isSpecialtyFilterEnabled
        allowedSpecialties: specialty_whitelist
        defaultSpecialty: default_specialty
        isLanguageFilterEnabled
        isLoadAllLocationsEnabled
        isLoadAllLocationsButtonEnabled
        isShowOnlyClaimedAccountsEnabled
        isShowProviderResultsEnabled
        partnerTagLabel
      }
      partnerTags {
        __typename
        id
        name
        description
        hexColor
      }
    }
  }
`
export const PARTNER_LOCATIONS = gql`
  query PartnerLocationsQuery($id: ID!) {
    partner(id: $id) {
      __typename
      id
      locations {
        ...locationFields
        practice {
          ...practiceFields
        }
      }
    }
  }
  ${locationFragment}
  ${practiceFragment}
`
export const RESOLVE_PROVIDER_LOCATION = gql`
  query ResolveProviderLocation($providerLocationId: ID!, $partnerId: ID!) {
    providerLocationMapping(
      providerLocationId: $providerLocationId
      partnerId: $partnerId
    ) {
      location {
        ...locationFields
        practice {
          ...practiceFields
        }
      }
      provider {
        ...providerFields
      }
    }
  }
  ${locationFragment}
  ${practiceFragment}
  ${providerFragment}
`

export const PROVIDER_DETAILS = gql`
  query ProviderDetails($id: ID!, $partnerId: ID) {
    provider(id: $id) {
      ...providerFields
      specialties {
        __typename
        name
      }
      partnerTier(partnerId: $partnerId) {
        __typename
        name
        description
        hexColor
        rank
      }
    }
  }
  ${providerFragment}
`
export const SPECIALTIES = gql`
  query Specialties($partnerId: ID) {
    specialties(partnerId: $partnerId) {
      __typename
      name
    }
  }
`
export const LOCATION_PROVIDERS = gql`
  query LocationProvidersQuery($id: ID!) {
    location(id: $id) {
      __typename
      id
      partnerTags {
        __typename
        id
        name
        description
        hexColor
      }
      providers {
        ...providerFields
        specialties {
          __typename
          name
        }
        partnerTier(partnerId: $id) {
          __typename
          name
          hexColor
          rank
          description
        }
        locations {
          __typename
          id
        }
      }
    }
  }
  ${providerFragment}
  ${locationFragment}
  ${practiceFragment}
`
export const SEARCH_LOCATIONS = gql`
  query SearchLocations($boundingBox: BoundingBox!, $specialty: String) {
    searchLocations(boundingBox: $boundingBox, specialty: $specialty) {
      locations {
        ...searchLocationFields
        practice {
          ...practiceFields
        }
        providers {
          ...providerSearchFields
          specialties {
            __typename
            name
          }
        }
      }
    }
  }
  ${searchLocationFragment}
  ${practiceFragment}
  ${providerSearchFragment}
`
export const SEARCH_PARTNER_LOCATIONS = gql`
  query SearchPartnerLocations(
    $boundingBox: BoundingBox!
    $specialty: String
    $partnerId: ID!
    $acceptsOnlineBooking: Boolean
  ) {
    searchLocations(
      boundingBox: $boundingBox
      specialty: $specialty
      partnerId: $partnerId
      acceptsOnlineBooking: $acceptsOnlineBooking
    ) {
      locations {
        ...searchLocationFields
        partnerTags {
          ...partnerTagFields
        }
        practice {
          ...practiceFields
        }
        providers {
          ...providerSearchFields
          specialties {
            __typename
            name
          }
          ...partnerTierFields
        }
      }
    }
  }
  ${searchLocationFragment}
  ${practiceFragment}
  ${providerSearchFragment}
  ${partnerTierFragment}
  ${partnerTagFragment}
`
export const SEARCH_PARTNER_PROVIDERS = gql`
  query SearchPartnerLocations(
    $boundingBox: BoundingBox!
    $specialty: String
    $partnerId: ID!
    $acceptsOnlineBooking: Boolean
  ) {
    searchLocations(
      boundingBox: $boundingBox
      specialty: $specialty
      partnerId: $partnerId
      acceptsOnlineBooking: $acceptsOnlineBooking
    ) {
      locations {
        ...searchLocationFields
        partnerTags {
          ...partnerTagFields
        }
        practice {
          ...practiceFields
        }
        providers {
          ...providerSearchFields
          specialties {
            __typename
            name
          }
          ...partnerTierFields
        }
      }
    }
  }
  ${searchLocationFragment}
  ${practiceFragment}
  ${providerSearchFragment}
  ${partnerTierFragment}
  ${partnerTagFragment}
`
